import { useState, useEffect, useMemo } from 'react';
import { getOrdersAgentWise } from '@/services/reportsService';
import { toastMessage } from '@/utils/toast-message';
import { Users, RefreshCw, Info } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface AgentWiseData {
  agent: string;
  order_count: number;
  total_revenue: number;
  paid_revenue: number;
  paid_orders: number;
  tags_used: string;
}

const PALETTE = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f97316',
  '#eab308', '#22c55e', '#14b8a6', '#3b82f6',
  '#ef4444', '#a855f7', '#06b6d4', '#84cc16',
];

const fmt = (v: number) => `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const AgentWiseReport = () => {
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<AgentWiseData[]>([]);
  const [chartMode, setChartMode] = useState<'orders' | 'revenue'>('orders');

  const fetchReport = async () => {
    if (!fromDate || !toDate) { toastMessage('Please select both dates', 'error'); return; }
    setLoading(true);
    try {
      const res = await getOrdersAgentWise({ from_date: fromDate, to_date: toDate });
      if (res.success) setData(res.data as AgentWiseData[]);
      else toastMessage('Failed to fetch report', 'error');
    } catch (e: any) {
      toastMessage(e.message || 'Error fetching report', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); }, []);

  const sorted = useMemo(() =>
    [...data].sort((a, b) => chartMode === 'orders'
      ? b.order_count - a.order_count
      : b.total_revenue - a.total_revenue),
    [data, chartMode]
  );

  const chartData = useMemo(() => ({
    labels: sorted.map(r => r.agent),
    datasets: [{
      label: chartMode === 'orders' ? 'Orders' : 'Revenue (₹)',
      data: sorted.map(r => chartMode === 'orders' ? r.order_count : Math.round(r.total_revenue)),
      backgroundColor: sorted.map((_, i) => PALETTE[i % PALETTE.length]),
      borderRadius: 4,
    }],
  }), [sorted, chartMode]);

  const maxOrders = useMemo(() => Math.max(...data.map(r => r.order_count), 1), [data]);

  return (
    <div className="h-full overflow-y-auto bg-gray-50 dark:bg-gray-950">

      {/* Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-brandColor rounded-lg text-white">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Orders by Agent</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">Agent performance derived from Shopify order tags</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
              className="px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
              className="px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
            <button onClick={fetchReport} disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-brandColor text-white text-sm rounded-lg hover:bg-brandColor/90 disabled:opacity-50 font-medium transition-all">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Loading…' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-5">

        {/* Explanation */}
        <div className="flex gap-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
          <Info className="h-4 w-4 text-blue-500 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800 dark:text-blue-300">
            <p className="font-semibold mb-0.5">About this report</p>
            <p className="text-blue-700 dark:text-blue-400 leading-relaxed">
              Orders grouped by agent, derived from <strong>Shopify order tags</strong>. The agent name is extracted as the last segment after <em>'-'</em> in a managed CRM tag
              (e.g. <em>Sales-BPO-Abrar → Abrar</em>). Payment-type tags like COD are excluded.
              For full CRM agent metrics — leads, conversion %, dispositions, response time — see <strong>Agent Performance</strong>.
            </p>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center h-48">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-brandColor" />
              <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading report…</p>
            </div>
          </div>
        )}

        {!loading && data.length > 0 && (
          <>
            {/* Chart */}
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-gray-800 dark:text-white">
                  Agent Performance — {chartMode === 'orders' ? 'Order Volume' : 'Revenue'}
                </h2>
                <div className="flex rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
                  <button onClick={() => setChartMode('orders')}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${chartMode === 'orders' ? 'bg-brandColor text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                    Orders
                  </button>
                  <button onClick={() => setChartMode('revenue')}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${chartMode === 'revenue' ? 'bg-brandColor text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                    Revenue
                  </button>
                </div>
              </div>
              <div style={{ height: Math.max(280, sorted.length * 28) }}>
                <Bar
                  data={chartData}
                  options={{
                    indexAxis: 'y',
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          label: ctx => chartMode === 'revenue'
                            ? ` ${fmt(ctx.parsed.x ?? 0)}`
                            : ` ${ctx.parsed.x ?? 0} orders`,
                        },
                      },
                    },
                    scales: {
                      x: { grid: { color: 'rgba(0,0,0,0.04)' }, ticks: { font: { size: 11 } } },
                      y: { grid: { display: false }, ticks: { font: { size: 11 } } },
                    },
                  }}
                />
              </div>
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-800 dark:text-white">Agent Details</h2>
                <span className="text-xs text-gray-400 dark:text-gray-500">{data.length} agents</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800/60 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="px-5 py-3 text-left text-gray-500 dark:text-gray-400">#</th>
                      <th className="px-5 py-3 text-left text-gray-500 dark:text-gray-400">Agent</th>
                      <th className="px-5 py-3 text-left text-gray-500 dark:text-gray-400 min-w-[160px]">Orders</th>
                      <th className="px-5 py-3 text-right text-gray-500 dark:text-gray-400">Revenue</th>
                      <th className="px-5 py-3 text-left text-gray-500 dark:text-gray-400">Tags Used</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {sorted.map((row, idx) => {
                      const orderPct = Math.round((row.order_count / maxOrders) * 100);
                      return (
                        <tr key={row.agent} className="hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors">
                          <td className="px-5 py-3.5 text-xs text-gray-400 dark:text-gray-500 font-mono">{idx + 1}</td>
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-2.5">
                              <div
                                className="h-7 w-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                                style={{ backgroundColor: PALETTE[idx % PALETTE.length] }}
                              >
                                {row.agent.charAt(0).toUpperCase()}
                              </div>
                              <span className="font-medium text-gray-900 dark:text-white">{row.agent}</span>
                            </div>
                          </td>
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-sm text-gray-900 dark:text-white w-8 text-right flex-shrink-0">
                                {row.order_count}
                              </span>
                              <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all"
                                  style={{ width: `${orderPct}%`, backgroundColor: PALETTE[idx % PALETTE.length] }}
                                />
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-3.5 text-right font-medium text-gray-900 dark:text-white">
                            {fmt(row.total_revenue)}
                          </td>
                          <td className="px-5 py-3.5 max-w-xs">
                            <span className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
                              {row.tags_used}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {!loading && data.length === 0 && (
          <div className="flex items-center justify-center h-48 text-gray-400 dark:text-gray-500">
            No agent data found for the selected period.
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentWiseReport;
